import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, FileText, Loader2, CheckCircle2, Eye, PhoneCall, MailCheck, AlertTriangle, Download, CalendarClock, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PacketPipeline } from "./PacketPipeline";
import { PublicPacketToggle } from "./PublicPacketToggle";

const kb = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const ago = (iso) => {
  const h = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m ago`;
  if (h < 48) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
};
const fmtDate = (iso) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export function CarrierPacketPanel({ user, onSent }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ to_email: "", broker_name: "", note: "" });
  const [sending, setSending] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [flash, setFlash] = useState(null);
  const rowRefs = useRef({});

  const load = () => api.get("/account/carrier-packet").then(({ data: d }) => { setData(d); setSelected(d.selected_ids || []); }).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const jumpTo = (id) => {
    setShowAll(true);
    setFlash(id);
    setTimeout(() => rowRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    setTimeout(() => setFlash(null), 2500);
  };

  if (!user) {
    return (
      <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3.5 text-sm text-white/60" data-testid="carrier-packet-anon">
        <Link to="/signup" className="text-[#FF5A1F] font-bold hover:underline">Create a free account</Link> to store your COI, W-9 and authority letter once and email the whole packet to any broker in one tap.
      </div>
    );
  }
  if (!data) return null;

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    setSelected(next);
    api.put("/account/carrier-packet", { doc_ids: next }).then(load).catch(() => {});
  };

  const setExpiry = async (docId, expires_on) => {
    try {
      await api.patch(`/account/carrier-packet/docs/${docId}/expiry`, { expires_on });
      await load();
      if (expires_on) toast.success(`Expiration saved — we'll warn you 30 days before ${fmtDate(expires_on)}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save date"); }
  };

  const markReplied = async (id) => {
    try { await api.post(`/account/carrier-packet/sends/${id}/replied`); await load(); }
    catch { toast.error("Couldn't update"); }
  };

  const logCall = async (id, payload) => {
    try {
      await api.post(`/account/carrier-packet/sends/${id}/call`, payload);
      toast.success(payload.outcome === "set_up" ? "Set up logged — nice work 🚛" : "Call logged");
      await load();
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't log call"); return false; }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!form.to_email.trim()) return;
    setSending(true);
    try {
      const { data: r } = await api.post("/account/carrier-packet/send", form);
      if (r.sent) {
        toast.success(`Packet sent to ${r.to_email} (${r.doc_count} file${r.doc_count === 1 ? "" : "s"})`);
        setForm({ to_email: "", broker_name: "", note: "" });
        onSent?.();
        await load();
      } else toast.error(r.reason === "not_configured" ? "Email isn't configured on this server yet" : "Send failed — try again");
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const inp = "rounded-md bg-black/40 border border-white/10 focus:border-[#FF5A1F] outline-none px-3 py-2 text-sm text-white placeholder:text-white/25 w-full";
  const c = data.company || {};
  const warn = data.coi_warning;
  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3.5 sm:p-4" data-testid="carrier-packet-panel">
      {warn && (
        <div className={`flex items-start gap-2 text-xs rounded-lg border px-3 py-2 mb-3 ${warn.expiry_state === "expired" ? "border-red-500/40 bg-red-500/10 text-red-200" : "border-amber-500/40 bg-amber-500/10 text-amber-100"}`} data-testid="carrier-packet-coi-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {warn.expiry_state === "expired"
              ? <><b>{warn.label}</b> expired {fmtDate(warn.expires_on)} — sending is blocked until you upload the renewed certificate.</>
              : <><b>{warn.label}</b> expires in <b>{warn.days_left} day{warn.days_left === 1 ? "" : "s"}</b> ({fmtDate(warn.expires_on)}). Get the renewed certificate from your agent now so brokers never get a stale COI.</>}
            {" "}<Link to="/app/vault" className="underline font-bold">Upload renewal</Link>
          </span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2">In the packet</div>
          {data.docs.length === 0 ? (
            <p className="text-sm text-white/60">No documents yet. <Link to="/app/vault" className="text-[#FF5A1F] font-bold hover:underline" data-testid="carrier-packet-upload-link">Upload your COI, W-9 and MC authority letter</Link> to the Docs Vault and they'll show up here.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="carrier-packet-docs">
              {data.docs.map((d) => <DocRow key={d.id} d={d} checked={selected.includes(d.id)} onToggle={() => toggle(d.id)} onExpiry={(v) => setExpiry(d.id, v)} />)}
            </ul>
          )}
          {data.missing?.length > 0 && data.docs.length > 0 && (
            <p className="text-xs text-amber-300/90 mt-2" data-testid="carrier-packet-missing">Missing: {data.missing.join(", ")} — <Link to="/app/vault" className="underline">add to vault</Link></p>
          )}
          <div className="mt-3 text-xs text-white/50 leading-relaxed" data-testid="carrier-packet-company">
            Email includes: <b className="text-white/80">{c.legal_name || "your company name"}</b>{c.mc_number && <> · {c.mc_number}</>}{c.dot_number && <> · USDOT {c.dot_number}</>}{c.phone && <> · {c.phone}</>} — replies go to {c.email}.
          </div>
        </div>
        <form onSubmit={send} className="space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2">Send to a broker</div>
          <input className={inp} type="email" required placeholder="broker@brokerage.com" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} data-testid="carrier-packet-email" />
          <input className={inp} placeholder="Broker's first name (optional)" value={form.broker_name} onChange={(e) => setForm({ ...form, broker_name: e.target.value })} data-testid="carrier-packet-broker-name" />
          <textarea className={`${inp} min-h-[64px]`} placeholder="Note (optional) — e.g. 'Per our call, ready for the Sioux Falls → Denver loads Monday.'" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="carrier-packet-note" />
          <button type="submit" disabled={sending || selected.length === 0 || warn?.expiry_state === "expired"} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors" data-testid="carrier-packet-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send packet ({selected.length} file{selected.length === 1 ? "" : "s"})
          </button>
        </form>
      </div>
      <PacketPipeline pipeline={data.pipeline} onJump={jumpTo} />
      <PublicPacketToggle page={data.public_page} onChange={(p) => setData((d) => ({ ...d, public_page: p }))} />
      {data.recent_sends?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/[0.06]">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Sent packets · opens & replies</div>
            <span className="text-[11px] text-white/40 hidden sm:inline" data-testid="carrier-packet-open-alert-hint"><Smartphone className="w-3 h-3 inline -mt-0.5" /> We text you the moment a broker opens one</span>
            {data.recent_sends.length > 5 && (
              <button type="button" onClick={() => setShowAll((v) => !v)} className="text-[11px] font-bold text-white/55 hover:text-white" data-testid="carrier-packet-show-all">
                {showAll ? "Show recent 5" : `Show all ${data.recent_sends.length}`}
              </button>
            )}
          </div>
          <ul className="space-y-1.5" data-testid="carrier-packet-recent">
            {(showAll ? data.recent_sends : data.recent_sends.slice(0, 5)).map((s) => (
              <SendRow key={s.id} s={s} hours={data.followup_hours} flash={flash === s.id} rowRef={(el) => { rowRefs.current[s.id] = el; }}
                onMarkReplied={() => markReplied(s.id)} onLogCall={(p) => logCall(s.id, p)} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DocRow({ d, checked, onToggle, onExpiry }) {
  const tone = d.expiry_state === "expired" ? "text-red-300" : d.expiry_state === "expiring_soon" ? "text-amber-300" : "text-white/40";
  return (
    <li className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      <label className="flex items-center gap-2.5 text-sm cursor-pointer min-w-0">
        <input type="checkbox" checked={checked} onChange={onToggle} className="accent-[#FF5A1F]" data-testid={`carrier-packet-doc-${d.id}`} />
        <FileText className="w-3.5 h-3.5 text-white/40 shrink-0" />
        <span className="text-white truncate">{d.label}</span>
        <span className="text-white/40 text-xs shrink-0">{d.tag_label} · {kb(d.size_bytes)}</span>
      </label>
      {d.tag === "coi" && (
        <span className="inline-flex items-center gap-1.5 text-[11px] ml-auto">
          <span className={tone} data-testid={`carrier-packet-doc-expiry-state-${d.id}`}>
            {d.expiry_state === "expired" ? "expired" : d.expiry_state === "expiring_soon" ? `${d.days_left}d left` : d.expires_on ? `valid ${d.days_left}d` : "expires?"}
          </span>
          <input type="date" value={d.expires_on || ""} onChange={(e) => onExpiry(e.target.value)} className="rounded-md bg-black/40 border border-white/10 px-1.5 py-0.5 text-[11px] text-white [color-scheme:dark]" aria-label="COI expiration date" data-testid={`carrier-packet-doc-expiry-${d.id}`} />
        </span>
      )}
    </li>
  );
}

const OUTCOMES = [
  { key: "set_up", label: "Set up ✅" },
  { key: "voicemail", label: "Voicemail" },
  { key: "callback", label: "Call back…" },
  { key: "not_onboarding", label: "Not onboarding new MCs" },
  { key: "wrong_contact", label: "Wrong contact" },
];

function SendRow({ s, hours, flash, rowRef, onMarkReplied, onLogCall }) {
  const [logging, setLogging] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [callbackOn, setCallbackOn] = useState("");
  const who = s.broker_name ? `${s.broker_name} · ${s.to_email}` : s.to_email;
  const last = s.last_call;
  const closed = last && (last.outcome === "set_up" || last.outcome === "not_onboarding");

  const submit = async () => {
    if (!outcome) return;
    const ok = await onLogCall({ outcome, note, callback_on: outcome === "callback" ? callbackOn : "" });
    if (ok) { setLogging(false); setOutcome(""); setNote(""); setCallbackOn(""); }
  };

  const tone = s.needs_call ? "bg-amber-500/[0.08] border border-amber-500/30" : last?.outcome === "set_up" ? "bg-emerald-500/[0.06] border border-emerald-500/20" : "bg-white/[0.02]";
  return (
    <li ref={rowRef} className={`text-xs rounded-lg px-2.5 py-2 transition-shadow ${tone} ${flash ? "ring-2 ring-[#FF5A1F]" : ""}`} data-testid={`carrier-packet-send-${s.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-white/80 font-semibold truncate max-w-[260px]">{who}</span>
        <span className="text-white/40">{ago(s.sent_at)}</span>
        <span className="inline-flex items-center gap-1 text-white/55" data-testid={`carrier-packet-send-open-${s.id}`}>
          <Eye className={`w-3 h-3 ${s.opened_at ? "text-sky-300" : "text-white/25"}`} />
          {s.opened_at ? `opened ${s.open_count > 1 ? `×${s.open_count} ` : ""}${ago(s.opened_at)}` : "not opened yet"}
        </span>
        {s.open_alerted_at && (
          <span className="inline-flex items-center gap-1 text-[#FF5A1F]" title="We alerted you the moment they opened it" data-testid={`carrier-packet-send-alerted-${s.id}`}><Smartphone className="w-3 h-3" /> you were alerted {ago(s.open_alerted_at)}</span>
        )}
        {s.download_count > 0 && (
          <span className="inline-flex items-center gap-1 text-sky-200" data-testid={`carrier-packet-send-download-${s.id}`}><Download className="w-3 h-3" /> downloaded {s.download_count > 1 ? `×${s.download_count}` : ""}</span>
        )}
        {s.replied_at ? (
          <span className="inline-flex items-center gap-1 text-emerald-300 font-bold" data-testid={`carrier-packet-send-replied-${s.id}`}><MailCheck className="w-3 h-3" /> replied {ago(s.replied_at)}</span>
        ) : last ? (
          <span className={`inline-flex items-center gap-1 font-bold ${last.outcome === "set_up" ? "text-emerald-300" : last.outcome === "not_onboarding" ? "text-white/45" : "text-sky-200"}`} data-testid={`carrier-packet-send-lastcall-${s.id}`}>
            <PhoneCall className="w-3 h-3" /> {last.label}{last.callback_on ? ` ${fmtDate(last.callback_on)}` : ""} · {ago(last.at)}
          </span>
        ) : s.needs_call ? (
          <span className="inline-flex items-center gap-1 text-amber-200 font-bold" data-testid={`carrier-packet-send-call-${s.id}`}><PhoneCall className="w-3 h-3" /> no reply in {hours}h — call them</span>
        ) : (
          <span className="text-white/40" data-testid={`carrier-packet-send-waiting-${s.id}`}>awaiting reply · nudge at {hours}h</span>
        )}
        {s.callback_on && !closed && (
          <span className={`inline-flex items-center gap-1 ${s.callback_reminded_at ? "text-emerald-300/80" : "text-white/45"}`} data-testid={`carrier-packet-send-callback-${s.id}`}>
            <CalendarClock className="w-3 h-3" /> {s.callback_reminded_at ? `texted you ${fmtDate(s.callback_on)}` : `we'll text you the morning of ${fmtDate(s.callback_on)}`}
          </span>
        )}
        <span className="ml-auto flex items-center gap-3">
          {!closed && (
            <button type="button" onClick={() => setLogging((v) => !v)} className={`text-[11px] font-bold ${logging ? "text-[#FF5A1F]" : "text-white/55 hover:text-white"}`} data-testid={`carrier-packet-send-logcall-${s.id}`}>
              {logging ? "Cancel" : "Log call"}
            </button>
          )}
          <button type="button" onClick={onMarkReplied} className="text-[11px] text-white/45 hover:text-white underline-offset-2 hover:underline" data-testid={`carrier-packet-send-toggle-replied-${s.id}`}>
            {s.replied_at ? (s.replied_via === "manual" ? "Undo" : <CheckCircle2 className="w-3.5 h-3.5 inline text-emerald-400" />) : "Mark replied"}
          </button>
        </span>
      </div>
      {logging && (
        <div className="mt-2 pt-2 border-t border-white/[0.06] flex flex-wrap items-center gap-1.5" data-testid={`carrier-packet-call-form-${s.id}`}>
          {OUTCOMES.map((o) => (
            <button key={o.key} type="button" onClick={() => setOutcome(o.key)} className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${outcome === o.key ? "border-[#FF5A1F] bg-[#FF5A1F]/15 text-[#FF5A1F]" : "border-white/15 text-white/70 hover:border-white/40"}`} data-testid={`carrier-packet-call-outcome-${o.key}-${s.id}`}>{o.label}</button>
          ))}
          {outcome === "callback" && (
            <input type="date" value={callbackOn} onChange={(e) => setCallbackOn(e.target.value)} className="rounded-md bg-black/40 border border-white/10 px-1.5 py-0.5 text-[11px] text-white [color-scheme:dark]" data-testid={`carrier-packet-call-date-${s.id}`} />
          )}
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (who you spoke to, what they need)" className="flex-1 min-w-[180px] rounded-md bg-black/40 border border-white/10 px-2 py-1 text-[11px] text-white placeholder:text-white/25" data-testid={`carrier-packet-call-note-${s.id}`} />
          <button type="button" onClick={submit} disabled={!outcome || (outcome === "callback" && !callbackOn)} className="rounded-full px-3 py-1 text-[11px] font-bold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-40" data-testid={`carrier-packet-call-save-${s.id}`}>Save</button>
        </div>
      )}
      {s.calls?.length > 1 && (
        <div className="mt-1.5 text-[11px] text-white/35" data-testid={`carrier-packet-call-history-${s.id}`}>
          {s.calls.slice(0, -1).reverse().map((c, i) => <span key={i}>{i > 0 && " · "}{c.label} {ago(c.at)}{c.note ? ` — ${c.note}` : ""}</span>)}
        </div>
      )}
    </li>
  );
}
